import React, { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useAlert } from "context/AlertContext";
import Skeleton from "components/ui/Skeleton";
import BuilderSidebar, { BUILDER_SECTIONS } from "./BuilderSidebar";
import ContentBuilder from "./ContentBuilder";
import PropertiesPanel from "./PropertiesPanel";
import ReviewSection from "./ReviewSection";
import { InformationSection, AttachmentsSection } from "./ArticleSidePanels";
import { createBlock, blocksToContents, contentsToBlocks, getStatusMeta } from "./blocks.data";
import apiKnowledgeArticle from "../api";

const EMPTY_META = {
  description: "",
  knowledgeBaseId: null,
  order: "",
  attachments: [],
};

const STEP_KEYS = BUILDER_SECTIONS.map((s) => s.key);

const ArticleBuilder = ({ article, onBack }) => {
  const { showAlert } = useAlert();
  const isEditMode = !!article?.id;

  const [loadingArticle, setLoadingArticle] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [isActivated, setIsActivated] = useState(true);
  const [activeSection, setActiveSection] = useState("information");
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [meta, setMeta] = useState(EMPTY_META);
  const [bannerImage, setBannerImage] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [scheduledAt, setScheduledAt] = useState(null);
  const [publishedAt, setPublishedAt] = useState(null);
  const [knowledgeBaseOptions, setKnowledgeBaseOptions] = useState([]);
  const [loadingKnowledgeBases, setLoadingKnowledgeBases] = useState(true);

  useEffect(() => {
    apiKnowledgeArticle
      .getKnowledgeBasesForDropdown()
      .then((res) => {
        if (res.data.status) {
          setKnowledgeBaseOptions((res.data.data || []).map((kb) => ({ value: kb.id, label: kb.title })));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingKnowledgeBases(false));
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    setLoadingArticle(true);
    apiKnowledgeArticle
      .getById(article.id)
      .then((res) => {
        if (res.data.status) {
          const data = res.data.data;
          setTitle(data.title || "");
          setIsActivated(data.is_activated ?? true);
          setBannerImage(data.banner_image || "");
          setSlug(data.slug || "");
          setStatus(data.status || "DRAFT");
          setScheduledAt(data.scheduled_at ? new Date(data.scheduled_at) : null);
          setPublishedAt(data.published_at ? new Date(data.published_at) : null);
          setMeta({
            description: data.excerpt || "",
            knowledgeBaseId: data.knowledge_base_fid || null,
            order: data.order ?? "",
            attachments: (data.kb_attachments || []).map((a) => ({
              file_name: a.file_name,
              file_url: a.file_url,
              file_size: a.file_size,
              file_type: a.file_type,
            })),
          });
          setBlocks(contentsToBlocks(data.kb_contents));
        } else {
          showAlert({ type: "error", message: res.data?.message || "Failed to load article" });
        }
      })
      .catch((error) => {
        showAlert({ type: "error", message: error.response?.data?.message || "Failed to load article" });
      })
      .finally(() => setLoadingArticle(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id]);

  const updateMeta = (patch) => setMeta((prev) => ({ ...prev, ...patch }));

  const handleAddBlock = (type, atIndex) => {
    const block = createBlock(type);
    setBlocks((prev) => {
      const next = [...prev];
      next.splice(atIndex, 0, block);
      return next;
    });
    setSelectedBlockId(block.id);
  };

  const handleImportBlocks = (imported, mode) => {
    if (!imported.length) return;
    setBlocks((prev) => (mode === "replace" ? imported : [...prev, ...imported]));
    setSelectedBlockId(imported[0].id);
    showAlert({
      type: "success",
      message: `${imported.length} block${imported.length > 1 ? "s" : ""} imported.`,
    });
  };

  const handleBlockDataChange = (id, data) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, data } : b)));
  };

  const handleDuplicateBlock = (id) => {
    setBlocks((prev) => {
      const index = prev.findIndex((b) => b.id === id);
      if (index === -1) return prev;
      const duplicate = { ...createBlock(prev[index].type), data: { ...prev[index].data } };
      const next = [...prev];
      next.splice(index + 1, 0, duplicate);
      return next;
    });
  };

  const handleDeleteBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    setSelectedBlockId((prev) => (prev === id ? null : prev));
  };

  const moveBlock = (id, direction) => {
    setBlocks((prev) => {
      const index = prev.findIndex((b) => b.id === id);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const reorderBlock = (draggedId, targetId) => {
    setBlocks((prev) => {
      const dragIndex = prev.findIndex((b) => b.id === draggedId);
      const targetIndex = prev.findIndex((b) => b.id === targetId);
      if (dragIndex === -1 || targetIndex === -1 || dragIndex === targetIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) || null;
  const knowledgeBaseLabel = knowledgeBaseOptions.find((kb) => kb.value === meta.knowledgeBaseId)?.label;

  const handleSubmit = async () => {
    if (!title.trim()) {
      showAlert({ type: "warning", message: "Title is required." });
      setActiveSection("information");
      return;
    }

    if (status === "SCHEDULED" && (!scheduledAt || scheduledAt.getTime() <= Date.now())) {
      showAlert({ type: "warning", message: "Pick a future date & time to schedule this article." });
      setActiveSection("review");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        excerpt: meta.description || "",
        banner_image: bannerImage || null,
        knowledge_base_fid: meta.knowledgeBaseId || null,
        order: meta.order !== "" && meta.order !== null && meta.order !== undefined ? parseInt(meta.order, 10) : undefined,
        is_activated: isActivated,
        status,
        scheduled_at: status === "SCHEDULED" ? scheduledAt.toISOString() : null,
        contents: blocksToContents(blocks),
        attachments: meta.attachments,
      };

      const response = isEditMode
        ? await apiKnowledgeArticle.update(article.id, payload)
        : await apiKnowledgeArticle.create(payload);

      if (response.data.status) {
        showAlert({
          type: "success",
          message: response.data?.message || `Article ${isEditMode ? "updated" : "created"} successfully`,
        });
        onBack();
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({ type: "error", message: error.response?.data?.message || "Something went wrong" });
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = STEP_KEYS.indexOf(activeSection);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEP_KEYS.length - 1;
  const goToStep = (delta) => setActiveSection(STEP_KEYS[stepIndex + delta]);

  if (loadingArticle) {
    return (
      <div className="h-full flex flex-col overflow-hidden bg-surface_alt p-6 gap-4">
        <Skeleton className="h-10 w-full" />
        <div className="flex-1 flex gap-4">
          <Skeleton className="hidden lg:block w-60 h-full" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface_alt">
      {/* Top header */}
      <div className="flex-shrink-0 flex items-center gap-2 sm:gap-4 px-3 sm:px-5 py-3 sm:py-3.5 border-b border-border bg-surface">
        <button
          type="button"
          onClick={onBack}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg text-xs font-medium text-text_secondary border border-border"
        >
          <ArrowLeft size={13} />
          <span className="hidden sm:inline">Back to Articles</span>
        </button>

        <div className="hidden sm:block w-px h-6 bg-border flex-shrink-0" />

        <div className="flex-1 min-w-0 flex items-center gap-2 sm:gap-2.5">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Untitled article"
            className="flex-1 min-w-0 text-[14px] sm:text-[15px] font-semibold bg-transparent outline-none text-text_primary placeholder:text-text_muted"
          />
          <span
            className={`flex-shrink-0 px-2 sm:px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${getStatusMeta(status).classes}`}
          >
            {getStatusMeta(status).label}
          </span>
          <span
            className={`hidden sm:inline-flex flex-shrink-0 px-2 sm:px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${
              isActivated
                ? "bg-success_bg text-success border-success_border"
                : "bg-danger_bg text-danger border-danger_border"
            }`}
          >
            {isActivated ? "Active" : "Inactive"}
          </span>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="flex-shrink-0 p-1.5 rounded-lg text-text_muted"
          title="Close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Step progress bar */}
      <div className="h-1 bg-border flex-shrink-0">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${((stepIndex + 1) / STEP_KEYS.length) * 100}%` }}
        />
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
        {/* Left stepper sidebar — horizontal bar below lg, vertical column at lg+ */}
        <div className="flex-shrink-0 lg:w-60 border-b lg:border-b-0 lg:border-r border-border bg-surface lg:overflow-y-auto">
          <BuilderSidebar activeSection={activeSection} onSectionChange={setActiveSection} />
        </div>

        {/* Center */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 sm:py-8">
            {activeSection === "information" && (
              <InformationSection
                meta={meta}
                onChange={updateMeta}
                knowledgeBaseOptions={knowledgeBaseOptions}
                loadingKnowledgeBases={loadingKnowledgeBases}
                bannerImage={bannerImage}
                onBannerImageChange={setBannerImage}
                slug={slug}
              />
            )}
            {activeSection === "content" && (
              <ContentBuilder
                blocks={blocks}
                selectedBlockId={selectedBlockId}
                onSelectBlock={setSelectedBlockId}
                onAddBlock={handleAddBlock}
                onImportBlocks={handleImportBlocks}
                onDuplicateBlock={handleDuplicateBlock}
                onDeleteBlock={handleDeleteBlock}
                onMoveBlockUp={(id) => moveBlock(id, -1)}
                onMoveBlockDown={(id) => moveBlock(id, 1)}
                onReorderBlock={reorderBlock}
              />
            )}
            {activeSection === "attachments" && (
              <AttachmentsSection
                attachments={meta.attachments}
                onChange={(attachments) => updateMeta({ attachments })}
              />
            )}
            {activeSection === "review" && (
              <ReviewSection
                title={title}
                blocks={blocks}
                meta={meta}
                bannerImage={bannerImage}
                knowledgeBaseLabel={knowledgeBaseLabel}
                isActivated={isActivated}
                onToggleActivated={setIsActivated}
                status={status}
                onStatusChange={setStatus}
                scheduledAt={scheduledAt}
                onScheduledAtChange={setScheduledAt}
                publishedAt={publishedAt}
                onSubmit={handleSubmit}
                isSubmitting={submitting}
                isEditMode={isEditMode}
              />
            )}
          </div>

          {/* Step navigation footer */}
          {!isLastStep && (
            <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-3.5 border-t border-border bg-surface">
              <button
                type="button"
                disabled={isFirstStep}
                onClick={() => goToStep(-1)}
                className="inline-flex items-center gap-1.5 px-3 sm:px-3.5 py-2 rounded-lg text-xs font-medium text-text_secondary border border-border disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={13} />
                Back
              </button>
              <button
                type="button"
                onClick={() => goToStep(1)}
                className="inline-flex items-center gap-1.5 px-3.5 sm:px-4 py-2 rounded-lg text-xs font-semibold text-white bg-primary shadow-sm"
              >
                <span className="sm:hidden">Continue</span>
                <span className="hidden sm:inline">Continue to {BUILDER_SECTIONS[stepIndex + 1]?.label}</span>
                <ArrowRight size={13} />
              </button>
            </div>
          )}
        </div>

        {/* Right properties panel — desktop only; only relevant while editing content blocks */}
        {activeSection === "content" && (
          <div className="hidden lg:block w-80 flex-shrink-0 border-l border-border bg-surface overflow-y-auto">
            <PropertiesPanel block={selectedBlock} onChange={handleBlockDataChange} />
          </div>
        )}
      </div>

      {/* Mobile/tablet properties drawer — replaces the fixed side column below lg */}
      {activeSection === "content" && selectedBlock && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-30 max-h-[75vh] flex flex-col bg-surface border-t border-border rounded-t-2xl shadow-2xl">
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-[13px] font-semibold text-text_primary">Edit Block</span>
            <button
              type="button"
              onClick={() => setSelectedBlockId(null)}
              className="p-1 rounded-md text-text_muted"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <PropertiesPanel block={selectedBlock} onChange={handleBlockDataChange} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleBuilder;
